import CookieIcon from '@mui/icons-material/Cookie';
import { Box, Button, Typography, Paper, Fade, Stack, alpha } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { type CookieConsentChoice, readCookieConsent, saveCookieConsent } from '@/lib/cookie-consent';

export function CookieConsent() {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!readCookieConsent()) {
      // Small delay for a better entrance
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleConsent = (choice: CookieConsentChoice) => {
    saveCookieConsent(choice);
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <Fade in={isVisible} timeout={800}>
      <Paper
        elevation={24}
        sx={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%) !important',
          width: '90%',
          maxWidth: 600,
          zIndex: 9999,
          p: { xs: 2, md: 3 },
          borderRadius: 4,
          background: (theme) => alpha(theme.palette.background.paper, 0.8),
          backdropFilter: 'blur(20px)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={3}
          alignItems="center"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 48,
              height: 48,
              borderRadius: '50%',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              color: 'primary.main',
            }}
          >
            <CookieIcon />
          </Box>

          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="body2" color="text.primary" sx={{ mb: 1, fontWeight: 500 }}>
              {t('common.cookies.message')}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} sx={{ minWidth: 'fit-content' }}>
            <Button
              variant="text"
              size="small"
              onClick={() => handleConsent('declined')}
              sx={{ textTransform: 'none', color: 'text.secondary' }}
            >
              {t('common.cookies.decline')}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleConsent('accepted')}
              sx={{
                textTransform: 'none',
                borderRadius: 2,
                px: 3,
                bgcolor: 'primary.main',
                '&:hover': {
                  bgcolor: 'primary.dark',
                }
              }}
            >
              {t('common.cookies.accept')}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Fade>
  );
}
